/**
 * Gift with Purchase (GWP) Module
 * Automatically adds/removes gift item based on:
 * - Cart subtotal threshold
 * - Specific trigger product in cart
 * - Products from specific trigger collection in cart
 */

theme.GWP = (function() {
  var config = {
    enabled: theme.settings.gwpEnabled,
    threshold: theme.settings.gwpThreshold ? (parseInt(theme.settings.gwpThreshold) * 100) : null, // Convert dollars to cents, null if not set
    variantId: theme.settings.gwpVariantId,
    label: theme.settings.gwpLabel,
    triggerProductId: theme.settings.gwpTriggerProductId || null,
    triggerCollectionProducts: theme.settings.gwpTriggerCollectionProducts || [],
    storageKey: 'gwp_user_removed',
    processing: false
  };

  // Session flag to track user intent
  var userRemovedGwp = function() {
    return sessionStorage.getItem(config.storageKey) === 'true';
  };

  var setUserRemovedFlag = function(value) {
    if (value) {
      sessionStorage.setItem(config.storageKey, 'true');
    } else {
      sessionStorage.removeItem(config.storageKey);
    }
  };

  // Calculate subtotal excluding GWP items
  var calculateSubtotal = function(cart) {
    var subtotal = 0;
    cart.items.forEach(function(item) {
      var isGwp = item.properties && item.properties._gwp === 'true';
      if (!isGwp) {
        subtotal += item.final_line_price;
      }
    });
    return subtotal;
  };

  // Check if GWP is in cart
  var gwpInCart = function(cart) {
    return cart.items.some(function(item) {
      return item.properties && item.properties._gwp === 'true';
    });
  };

  // Find GWP line item key
  var getGwpKey = function(cart) {
    var gwpItem = cart.items.find(function(item) {
      return item.properties && item.properties._gwp === 'true';
    });
    return gwpItem ? gwpItem.key : null;
  };

  // Add GWP to cart
  var addGwp = function() {
    if (config.processing) return Promise.resolve();
    config.processing = true;

    var formData = {
      items: [{
        id: config.variantId,
        quantity: 1,
        properties: {
          '_gwp': 'true'
        }
      }]
    };

    return fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(function(response) {
      return response.json();
    })
    .then(function(data) {
      if (!data.items) {
        // Failed silently (out of stock, etc.)
        config.processing = false;
        return null;
      }
      // Wait a moment before rebuilding to ensure cart state is updated
      setTimeout(function() {
        config.processing = false;
        // Rebuild cart drawer to show the GWP item
        document.dispatchEvent(new CustomEvent('cart:build'));
      }, 100);
      return data;
    })
    .catch(function(error) {
      config.processing = false;
      // Fail silently
      console.log('GWP add failed:', error);
      return null;
    });
  };

  // Remove GWP from cart
  var removeGwp = function(gwpKey) {
    if (config.processing) return Promise.resolve();
    config.processing = true;

    return theme.cart.changeItem(gwpKey, 0)
      .then(function() {
        config.processing = false;
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(function(error) {
        config.processing = false;
        console.log('GWP remove failed:', error);
      });
  };

  // Check if trigger product is in cart
  var hasTriggerProduct = function(cart) {
    if (!config.triggerProductId) return false;

    return cart.items.some(function(item) {
      // Skip GWP items themselves
      var isGwp = item.properties && item.properties._gwp === 'true';
      if (isGwp) return false;

      return item.product_id === config.triggerProductId;
    });
  };

  // Check if any product from trigger collection is in cart
  var hasTriggerCollectionProduct = function(cart) {
    if (!config.triggerCollectionProducts || config.triggerCollectionProducts.length === 0) return false;

    return cart.items.some(function(item) {
      // Skip GWP items themselves
      var isGwp = item.properties && item.properties._gwp === 'true';
      if (isGwp) return false;

      return config.triggerCollectionProducts.indexOf(item.product_id) !== -1;
    });
  };

  // Check if any trigger condition is met
  var meetsGwpConditions = function(cart) {
    // Check threshold (if configured)
    var meetsThreshold = false;
    if (config.threshold !== null) {
      var subtotal = calculateSubtotal(cart);
      meetsThreshold = subtotal >= config.threshold;
    }

    // Check trigger product (if configured)
    var meetsTriggerProduct = hasTriggerProduct(cart);

    // Check trigger collection (if configured)
    var meetsTriggerCollection = hasTriggerCollectionProduct(cart);

    // Return true if any condition is met
    return meetsThreshold || meetsTriggerProduct || meetsTriggerCollection;
  };

  // Main logic: Check cart and add/remove GWP as needed
  var checkCart = function() {
    if (!config.enabled || !config.variantId) return;
    if (config.processing) return;

    theme.cart.getCart()
      .then(function(cart) {
        var hasGwp = gwpInCart(cart);
        var meetsConditions = meetsGwpConditions(cart);
        var userRemoved = userRemovedGwp();

        // No conditions met: remove GWP and clear user flag
        if (!meetsConditions && hasGwp) {
          setUserRemovedFlag(false);
          var gwpKey = getGwpKey(cart);
          if (gwpKey) {
            removeGwp(gwpKey);
          }
        }
        // Conditions met: add GWP if not present and user hasn't removed it
        else if (meetsConditions && !hasGwp && !userRemoved) {
          addGwp();
        }
      })
      .catch(function(error) {
        console.log('GWP check failed:', error);
      });
  };

  // Listen for manual removal by user
  var watchForManualRemoval = function() {
    document.addEventListener('cart:quantity', function(evt) {
      var detail = evt.detail;
      if (!detail || detail.length < 2) return;

      var newQty = detail[1];

      // Check if removed item was GWP
      if (newQty === 0) {
        // Check the wrapper element for GWP data
        var wrapper = detail[2]; // QtySelector passes wrapper element
        if (wrapper) {
          var cartItem = wrapper.closest('.cart__item');
          if (cartItem) {
            // Check if this item has GWP properties
            var priceCol = cartItem.querySelector('.cart__gwp-free');
            if (priceCol) {
              // This was a GWP item, set flag
              setUserRemovedFlag(true);
            }
          }
        }
      }
    });
  };

  // Initialize
  var init = function() {
    if (!config.enabled || !config.variantId) return;

    // Check cart on page load
    checkCart();

    // Listen for product added to cart from PDP (Product Detail Page)
    document.addEventListener('ajaxProduct:added', function() {
      setTimeout(checkCart, 1000);
    });

    // Listen for cart build (fires from PLP collection-size-buttons.js)
    document.addEventListener('cart:build', function() {
      setTimeout(checkCart, 1000);
    });

    // Listen for cart updates (quantity changes, removals)
    document.addEventListener('cart:updated', function() {
      checkCart();
    });

    // Watch for manual GWP removal
    watchForManualRemoval();
  };

  return {
    init: init,
    checkCart: checkCart
  };
})();

// Auto-initialize when DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', theme.GWP.init);
} else {
  theme.GWP.init();
}
